import {
  ComparePeriodGranularity,
  DatePeriod
} from './m-compare-period-picker.types';

/** Inclusive selectable window; same shape as calendar min/max pairs. */
export interface PeriodBounds {
  minDate?: string | null;
  maxDate?: string | null;
}

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
] as const;

const QUARTER_RANGES = [
  'Jan - Mar',
  'Apr - Jun',
  'Jul - Sep',
  'Oct - Dec'
] as const;

export function parseIsoDate(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year!, month! - 1, day!);
}

export function formatIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function monthPeriod(year: number, monthIndex: number): DatePeriod {
  const start = new Date(year, monthIndex, 1);
  const end = new Date(year, monthIndex + 1, 0);
  return { startDate: formatIsoDate(start), endDate: formatIsoDate(end) };
}

export function quarterPeriod(year: number, quarterIndex: number): DatePeriod {
  const startMonth = quarterIndex * 3;
  const start = new Date(year, startMonth, 1);
  const end = new Date(year, startMonth + 3, 0);
  return { startDate: formatIsoDate(start), endDate: formatIsoDate(end) };
}

export function yearPeriod(year: number): DatePeriod {
  return {
    startDate: formatIsoDate(new Date(year, 0, 1)),
    endDate: formatIsoDate(new Date(year, 11, 31))
  };
}

export function weekPeriodContaining(date: Date, weekStartsOn = 0): DatePeriod {
  const day = date.getDay();
  const diff = (day - weekStartsOn + 7) % 7;
  const start = addDays(startOfDay(date), -diff);
  const end = addDays(start, 6);
  return { startDate: formatIsoDate(start), endDate: formatIsoDate(end) };
}

export function periodsEqual(a: DatePeriod, b: DatePeriod): boolean {
  return a.startDate === b.startDate && a.endDate === b.endDate;
}

export function isPeriodWithinBounds(
  period: DatePeriod,
  bounds: PeriodBounds
): boolean {
  const start = parseIsoDate(period.startDate);
  const end = parseIsoDate(period.endDate);
  if (bounds.minDate) {
    const min = parseIsoDate(bounds.minDate);
    if (end < min) return false;
  }
  if (bounds.maxDate) {
    const max = parseIsoDate(bounds.maxDate);
    if (start > max) return false;
  }
  return true;
}

/** True when the period exactly matches an allowlisted start/end pair. */
export function isPeriodAllowed(
  period: DatePeriod,
  allowedPeriods: DatePeriod[] | null | undefined
): boolean {
  if (!allowedPeriods || allowedPeriods.length === 0) {
    return false;
  }
  return allowedPeriods.some(
    (allowed) =>
      allowed.startDate === period.startDate &&
      allowed.endDate === period.endDate
  );
}

/** True when the period can be selected given inclusive bounds. */
export function isPeriodSelectable(
  period: DatePeriod,
  bounds: PeriodBounds,
  allowedPeriods?: DatePeriod[] | null
): boolean {
  if (
    allowedPeriods !== undefined &&
    !isPeriodAllowed(period, allowedPeriods)
  ) {
    return false;
  }
  if (!bounds.minDate && !bounds.maxDate) return true;
  const start = parseIsoDate(period.startDate);
  const end = parseIsoDate(period.endDate);
  if (bounds.minDate && end < parseIsoDate(bounds.minDate)) return false;
  if (bounds.maxDate && start > parseIsoDate(bounds.maxDate)) return false;
  // Require the whole period to fit when both ends are set tightly
  if (bounds.minDate && start < parseIsoDate(bounds.minDate)) return false;
  if (bounds.maxDate && end > parseIsoDate(bounds.maxDate)) return false;
  return true;
}

export function getMonthLabels(): readonly string[] {
  return MONTH_LABELS;
}

export function getQuarterRangeLabel(quarterIndex: number): string {
  return QUARTER_RANGES[quarterIndex] ?? '';
}

export function formatWeekLabel(startDate: string): string {
  const date = parseIsoDate(startDate);
  const month = MONTH_LABELS[date.getMonth()];
  return `Week of ${month} ${date.getDate()}, ${date.getFullYear()}`;
}

export function formatMonthLabel(startDate: string): string {
  const date = parseIsoDate(startDate);
  return `${MONTH_LABELS[date.getMonth()]} ${date.getFullYear()}`;
}

export function formatQuarterLabel(startDate: string): string {
  const date = parseIsoDate(startDate);
  const quarter = Math.floor(date.getMonth() / 3) + 1;
  return `Q${quarter} ${date.getFullYear()}`;
}

export function formatYearLabel(startDate: string): string {
  return String(parseIsoDate(startDate).getFullYear());
}

export function formatCompareTriggerLabel(
  granularity: ComparePeriodGranularity,
  current: DatePeriod,
  comparison: DatePeriod
): string {
  const formatOne = (period: DatePeriod): string => {
    switch (granularity) {
      case 'week':
        return formatWeekLabel(period.startDate);
      case 'month':
        return formatMonthLabel(period.startDate);
      case 'quarter':
        return formatQuarterLabel(period.startDate);
      case 'year':
        return formatYearLabel(period.startDate);
    }
  };
  return `${formatOne(current)} vs ${formatOne(comparison)}`;
}

export function yearsInBounds(bounds: PeriodBounds): number[] {
  const now = new Date().getFullYear();
  const minYear = bounds.minDate
    ? parseIsoDate(bounds.minDate).getFullYear()
    : now - 4;
  const maxYear = bounds.maxDate
    ? parseIsoDate(bounds.maxDate).getFullYear()
    : now;
  const years: number[] = [];
  for (let y = minYear; y <= maxYear; y++) {
    years.push(y);
  }
  return years;
}

export function yearRangeLabel(bounds: PeriodBounds): string {
  const years = yearsInBounds(bounds);
  if (years.length === 0) return '';
  if (years.length === 1) return String(years[0]);
  return `${years[0]} – ${years[years.length - 1]}`;
}

export interface PanelViewportInput {
  /** Absolute top (px, including scrollY) that PrimeNG assigned to the panel. */
  panelTop: number;
  panelHeight: number;
  viewportHeight: number;
  scrollY: number;
  /** Viewport pixels hidden by the sticky header (top nav + any alert banner). */
  occludedTop: number;
  /** Gap kept between the panel and the header / viewport bottom. Default 8. */
  margin?: number;
}

export interface PanelViewportResult {
  top: number;
  maxHeight: number;
}

export const PANEL_VIEWPORT_MARGIN = 8;

/**
 * Keep an absolutely positioned popover fully visible below the sticky header.
 *
 * PrimeNG flips a popover above its trigger when it does not fit below and, if
 * that would go negative, clamps it to the viewport top — where the sticky nav
 * covers it. This only moves the panel when it would be occluded or overflow;
 * a panel that already fits is returned unchanged.
 */
export function clampPanelToViewport(
  input: PanelViewportInput
): PanelViewportResult {
  const margin = input.margin ?? PANEL_VIEWPORT_MARGIN;
  const occludedTop = Math.max(0, input.occludedTop);
  const maxHeight = Math.max(
    0,
    input.viewportHeight - occludedTop - 2 * margin
  );
  const visibleHeight = Math.min(input.panelHeight, maxHeight);

  const minTop = input.scrollY + occludedTop + margin;
  const maxTop = input.scrollY + input.viewportHeight - margin - visibleHeight;
  const top = Math.min(
    Math.max(input.panelTop, minTop),
    Math.max(minTop, maxTop)
  );

  return { top, maxHeight };
}
